import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "./Constants";

const DisplayAssessments = (props) => {
  const [assessments, setAssessments] = useState([]);
  const [submissionFiles, setSubmissionFiles] = useState({});
  const [uploading, setUploading] = useState({});
  const [message, setMessage] = useState({});
  const enrollId = props.id;

  useEffect(() => {
    const fetchAssessments = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/assessments`);
        const filteredData = response.data.filter(item => item.enrollment_id == enrollId);
        setAssessments(filteredData);
      } catch (error) {
        console.error("Failed to fetch assessments", error);
      }
    };
    fetchAssessments();
  }, [enrollId]);

  const handleDownload = (filePath, title) => {
    if (!filePath) return;
      // const url = `${API_BASE_URL.replace('/api', '')}/storage/${filePath}`;
    const url = `${API_BASE_URL.replace('/api', '')}/public/storage/${filePath}`;
    window.open(url, '_blank');
  };

  const handleFileChange = (assessmentId, file) => {
    setSubmissionFiles(prev => ({
      ...prev,
      [assessmentId]: file
    }));
    setMessage(prev => ({
      ...prev,
      [assessmentId]: ""
    }));
  };

  const handleSubmitAssessment = async (assessmentId) => {
    const file = submissionFiles[assessmentId];
    if (!file) {
      setMessage(prev => ({
        ...prev,
        [assessmentId]: "Please select a file to submit."
      }));
      return;
    }

    setUploading(prev => ({
      ...prev,
      [assessmentId]: true
    }));

    const formData = new FormData();
    formData.append("submission_file", file);
    formData.append("_method", "POST"); // For Laravel update method

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/assessments/${assessmentId}/submit`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      // Update the assessment in state
      setAssessments(prev => prev.map(assessment => 
        assessment.id === assessmentId 
          ? { 
              ...assessment, 
              file_path_submission: response.data.file_path_submission,
              submission_date: response.data.submission_date,
              status: response.data.status
            } 
          : assessment
      ));

      setMessage(prev => ({
        ...prev,
        [assessmentId]: "Submission successful!"
      }));

      // Clear the file input
      setSubmissionFiles(prev => ({
        ...prev,
        [assessmentId]: null
      }));

      // Clear file input value
      const fileInput = document.getElementById(`file-input-${assessmentId}`);
      if (fileInput) fileInput.value = "";

    } catch (error) {
      setMessage(prev => ({
        ...prev,
        [assessmentId]: "Submission failed: " + (error.response?.data?.message || error.message)
      }));
    } finally {
      setUploading(prev => ({
        ...prev,
        [assessmentId]: false
      }));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { class: 'badge badge-warning', text: 'Pending' },
      'submitted': { class: 'badge badge-success', text: 'Submitted' },
      'graded': { class: 'badge badge-info', text: 'Graded' },
      'overdue': { class: 'badge badge-error', text: 'Overdue' }
    };
    
    const config = statusConfig[status] || { class: 'badge badge-outline', text: status };
    return <span className={config.class}>{config.text}</span>;
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    return new Date(dateTimeString).toLocaleString();
  };

  const isSubmissionAllowed = (assessment) => {
    if (assessment.status === 'submitted' || assessment.status === 'graded') {
      return false; // Already submitted or graded
    }

    if (assessment.due_date) {
      const dueDate = new Date(assessment.due_date);
      const now = new Date();
      return now <= dueDate; // Only allow submission before due date
    }
    
    return true; // No due date, always allowed
  };

  return (
    <div className="overflow-x-auto">
      <table className="table table-zebra w-full">
        <thead>
          <tr>
            <th>Title</th>
            <th>File Type</th>
            <th>Due Date</th>
            <th>Submission Date</th>
            <th>Status</th>
            <th>Download Assessment</th>
            <th>Submit Assessment</th>
          </tr>
        </thead>
        <tbody>
          {assessments.length === 0 ? (
            <tr>
              <td colSpan="7" className="text-center">No assessments found.</td>
            </tr>
          ) : (
            assessments.map((assessment) => (
              <tr key={assessment.id}>
                <td className="font-bold">{assessment.title}</td>
                <td>
                  {assessment.file_path ? (
                    <span className="badge badge-outline">
                      {assessment.file_path.split('.').pop().toUpperCase()}
                    </span>
                  ) : (
                    <span className="text-gray-500">No file</span>
                  )}
                </td>
                <td>
                  {assessment.due_date ? formatDateTime(assessment.due_date) : 'No due date'}
                </td>
                <td>
                  {assessment.submission_date ? formatDateTime(assessment.submission_date) : 'Not submitted'}
                </td>
                <td>{getStatusBadge(assessment.status)}</td>
                <td>
                  {assessment.file_path ? (
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleDownload(assessment.file_path, assessment.title)}
                      title="Download Assessment"
                    >
                      Download
                    </button>
                  ) : (
                    <span className="text-gray-500">No file</span>
                  )}
                </td>
                <td>
                  <div className="space-y-2 min-w-[200px]">
                    {assessment.file_path_submission ? (
                      <div className="space-y-2">
                        <button
                          className="btn btn-sm btn-secondary w-full"
                          onClick={() => handleDownload(assessment.file_path_submission, `${assessment.title}_submission`)}
                          title="Download Submission"
                        >
                          View Submission
                        </button>
                        <div className="text-xs text-success">Already submitted</div>
                      </div>
                    ) : isSubmissionAllowed(assessment) ? (
                      <div className="space-y-2">
                        <input
                          id={`file-input-${assessment.id}`}
                          type="file"
                          className="file-input file-input-bordered file-input-sm w-full"
                          onChange={(e) => handleFileChange(assessment.id, e.target.files[0])}
                          disabled={uploading[assessment.id]}
                        />
                        <button
                          className="btn btn-sm btn-success w-full"
                          onClick={() => handleSubmitAssessment(assessment.id)}
                          disabled={uploading[assessment.id] || !submissionFiles[assessment.id]}
                        >
                          {uploading[assessment.id] ? (
                            <span className="loading loading-spinner loading-sm"></span>
                          ) : (
                            'Submit'
                          )}
                        </button>
                        {message[assessment.id] && (
                          <div className={`text-xs ${message[assessment.id].includes('failed') ? 'text-error' : 'text-success'}`}>
                            {message[assessment.id]}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-error">
                        {assessment.due_date && new Date(assessment.due_date) < new Date()
                          ? 'Submission overdue' 
                          : 'Submission not allowed'
                        }
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DisplayAssessments;